import { Action, createAction, handleActions } from "redux-actions";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import {
  EsquemaCotizacion,
  EsquemaCotizacionItem,
  GetEsquemasCotizacionesResponse,
  PatchCoberturaProductorRequest,
} from "~api/types";
import { ApiError, Services } from "./types";

export interface EsquemasState {
  /** Nunca se lee; queda del estado original. */
  data: unknown[];
  esquemas?: EsquemaCotizacionItem[];
  hasChanged: boolean;
  esquemaSelected: EsquemaCotizacionItem | null | undefined;
  lastEsquemaSelected: string | null | undefined;
  failed: boolean;
}

interface GetEsquemasCompletedPayload {
  esquemas: EsquemaCotizacionItem[];
  esquemaSelected: EsquemaCotizacionItem | null | undefined;
}

const ERROR_CODES: Record<string, string> = {
  CoberturaNoPerteneceCompania:
    "Alguna de las coberturas ingresadas no es válida para la compañía",
};

const postEsquemasInited = createAction(
  "MODIFICAR_ESQUEMAS_INITED",
  (data: { id: string; esquemasCotizacion: EsquemaCotizacion[] }) => data
);
const postEsquemasCompleted = createAction("MODIFICAR_ESQUEMAS_COMPLETED");
const postEsquemasFailed = createAction(
  "MODIFICAR_ESQUEMAS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const patchEsquemasInited = createAction(
  "MODIFICAR_COBERTURA_ESQUEMAS_PRENDARIOS_INITED"
);
const patchEsquemasCompleted = createAction(
  "MODIFICAR_COBERTURA_ESQUEMAS_PRENDARIOS_COMPLETED"
);
const patchEsquemasFailed = createAction(
  "MODIFICAR_COBERTURA_ESQUEMAS_PRENDARIOS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getEsquemasInited = createAction("OBTENER_ESQUEMAS_INITED");
const getEsquemasCompleted = createAction(
  "OBTENER_ESQUEMAS_COMPLETED",
  (
    response: GetEsquemasCotizacionesResponse | null | undefined,
    lastEsquemaSelected: string | null | undefined
  ): GetEsquemasCompletedPayload => {
    let esquemas: EsquemaCotizacionItem[] = [];
    let esquemaSelected: EsquemaCotizacionItem | null | undefined = null;
    if (
      typeof response !== "undefined" &&
      response !== null &&
      response.total !== 0
    ) {
      esquemas = response.items;

      esquemaSelected = !lastEsquemaSelected
        ? esquemas[0]
        : esquemas.find((esquema) => {
            return esquema.id === lastEsquemaSelected;
          });
    }

    return { esquemas, esquemaSelected };
  }
);
const getEsquemasFailed = createAction(
  "OBTENER_ESQUEMAS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const updateEsquemas = createAction(
  "UPDATE_ESQUEMAS",
  (esquemas: EsquemaCotizacionItem[]) => ({
    esquemas,
  })
);

const setEsquemaSelected = createAction(
  "SET_ESQUEMA_SELECTED",
  (esquemaSelected: EsquemaCotizacionItem | null | undefined) => ({
    esquemaSelected,
  })
);

const setHasChanged = createAction(
  "SET_HAS_CHANGED",
  (hasChanged: boolean) => ({
    hasChanged,
  })
);

const getErrorMessage = (error: unknown): string => {
  const message = (error as ApiError).responseStatus.message;
  return ERROR_CODES[message]
    ? ERROR_CODES[message]
    : "Ocurrió un error inesperado. Intente nuevamente.";
};

const postEsquemas = (id: string, esquemasCotizacion: EsquemaCotizacion[]) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(postEsquemasInited({ id, esquemasCotizacion }));

      const data = {
        id: id,
        esquemasCotizacion: esquemasCotizacion,
      };

      await services.api.esquemas().postEsquemas(data);

      dispatch(postEsquemasCompleted());
    } catch (error) {
      dispatch(queueMessage(getErrorMessage(error)));
      dispatch(postEsquemasFailed(error));
    }
  };
};

const patchEsquemas = (data: PatchCoberturaProductorRequest) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(patchEsquemasInited());

      await services.api.esquemas().patchEsquemas(data);

      dispatch(patchEsquemasCompleted());
    } catch (error) {
      dispatch(queueMessage(getErrorMessage(error)));
      dispatch(postEsquemasFailed(error));
    }
  };
};

const esquemasInitialState: EsquemasState = {
  data: [],
  hasChanged: false,
  esquemaSelected: null,
  lastEsquemaSelected: null,
  failed: false,
};

const esquemasReducer = handleActions<EsquemasState, any>(
  {
    [postEsquemasInited.toString()]: (state) => {
      return {
        ...state,
      };
    },
    [postEsquemasCompleted.toString()]: (state) => {
      return {
        ...state,
        hasChanged: false,
      };
    },
    [postEsquemasFailed.toString()]: (state) => {
      return {
        ...state,
      };
    },
    [patchEsquemasInited.toString()]: (state) => {
      return {
        ...state,
      };
    },
    [patchEsquemasCompleted.toString()]: (state) => {
      return {
        ...state,
        hasChanged: false,
      };
    },
    [patchEsquemasFailed.toString()]: (state) => {
      return {
        ...state,
      };
    },
    [getEsquemasInited.toString()]: (state) => {
      return {
        ...state,
        esquemaSelected: null,
        hasChanged: false,
      };
    },
    [getEsquemasCompleted.toString()]: (
      state,
      action: Action<GetEsquemasCompletedPayload>
    ) => {
      return {
        ...state,
        esquemas: [...action.payload.esquemas],
        esquemaSelected: action.payload.esquemaSelected,
        failed: false,
      };
    },
    [getEsquemasFailed.toString()]: (state) => {
      return {
        ...state,
        failed: true,
      };
    },
    [updateEsquemas.toString()]: (
      state,
      action: Action<{ esquemas: EsquemaCotizacionItem[] }>
    ) => {
      return {
        ...state,
        esquemas: action.payload.esquemas,
        hasChanged: true,
      };
    },
    [setEsquemaSelected.toString()]: (
      state,
      action: Action<{
        esquemaSelected: EsquemaCotizacionItem | null | undefined;
      }>
    ) => {
      return {
        ...state,
        esquemaSelected: action.payload.esquemaSelected,
        lastEsquemaSelected:
          action.payload.esquemaSelected && action.payload.esquemaSelected.id,
      };
    },
    [setHasChanged.toString()]: (
      state,
      action: Action<{ hasChanged: boolean }>
    ) => {
      return {
        ...state,
        hasChanged: action.payload.hasChanged,
      };
    },
  },
  esquemasInitialState
);

const getEsquemas = () => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(getEsquemasInited());
      const response = await services.api.esquemas().getEsquemas();

      dispatch(
        getEsquemasCompleted(
          response,
          getState().esquemasReducer.lastEsquemaSelected
        )
      );
    } catch (error) {
      console.error(error);
      dispatch(getEsquemasFailed(error));
    }
  };
};

export { esquemasReducer };
export {
  postEsquemas,
  getEsquemas,
  updateEsquemas,
  setEsquemaSelected,
  setHasChanged,
  patchEsquemas,
};
